/**
 * AssessmentInstructionsContent
 *
 * Test-agnostic orientation copy for candidates — three sections:
 *   1. Purpose — what this kind of reflection is for
 *   2. What to expect — the high-level flow (TP step is conditional)
 *   3. How to navigate the screens — concrete on-screen controls
 *
 * Optionally shows the Sc.3 comparison disclosure at the bottom.
 *
 * Single source of truth, rendered in two places:
 *   • Inline on CandidateWelcomePage for first-time orientation
 *   • Inside AssessmentInstructionsDialog (top-bar "How this works"
 *     button) for mid-flow refreshers
 */

import React from "react";
import {
  Target,
  MousePointerClick,
  ArrowLeftRight,
  Save,
  Clock,
} from "lucide-react";

export interface AssessmentInstructionsContentProps {
  /** Sc.4/5 candidates don't fill TP — collapse the flow to a single step. */
  skipTP: boolean;
  /** Sc.3 candidates see an anonymized-comparison disclosure. */
  hasComparison?: boolean;
  /** Tighten section spacing when rendered inside a dialog. */
  density?: "comfortable" | "compact";
}

export function AssessmentInstructionsContent({
  skipTP,
  hasComparison = false,
  density = "comfortable",
}: AssessmentInstructionsContentProps) {
  const steps = [
    ...(!skipTP
      ? [
          {
            num: 1,
            label: "Time Prioritization",
            desc: "Distribute 100 points across leadership activities to show where your attention goes. This shapes which scenarios you see next.",
          },
        ]
      : []),
    {
      num: skipTP ? 1 : 2,
      label: "Scenario-Based Assessment",
      desc: "Respond to real leadership situations by allocating 100 points across the options that best reflect your approach.",
    },
  ];

  const sectionGap = density === "compact" ? "space-y-6" : "space-y-8";

  return (
    <div className={sectionGap}>
      {/* Purpose */}
      <section>
        <div className="flex items-center gap-2 mb-2">
          <Target size={16} className="text-brand-steel" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-gray-900">Purpose</h3>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed mb-3">
          This reflection helps you see how your leadership shows up across real situations. It
          looks at:
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-3">
          <li>Where you choose to invest your attention</li>
          <li>How you respond to competing priorities</li>
          <li>The instincts, behaviors, and methods that shape your decisions</li>
        </ul>
        <p className="text-sm text-gray-700 leading-relaxed">
          The goal is clarity — a view of how your leadership operates in context, not how it's
          described in theory.
        </p>
      </section>

      {/* What to expect */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">What to expect</h3>
        <div className="bg-brand-panel rounded-lg p-5 space-y-3">
          {steps.map((step) => (
            <div key={step.num} className="flex items-start gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold flex items-center justify-center mt-0.5">
                {step.num}
              </span>
              <p className="text-sm text-gray-900">
                <span className="font-medium">{step.label}</span>
                <span className="text-gray-600"> — {step.desc}</span>
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How to navigate */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">How to navigate the screens</h3>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <MousePointerClick
              size={16}
              className="text-brand-steel shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <p className="text-sm text-gray-700 leading-relaxed">
              Each question shows you a set of options. Allocate exactly{" "}
              <span className="font-medium text-gray-900">100 points</span> across them based on
              how strongly each one reflects your approach — partial points are encouraged.
            </p>
          </li>
          <li className="flex items-start gap-3">
            <ArrowLeftRight
              size={16}
              className="text-brand-steel shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <p className="text-sm text-gray-700 leading-relaxed">
              Use <span className="font-medium text-gray-900">Next</span> to move forward once
              your points add up to 100. The progress bar at the top tracks where you are.
            </p>
          </li>
          <li className="flex items-start gap-3">
            <Save size={16} className="text-brand-steel shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-gray-700 leading-relaxed">
              You can <span className="font-medium text-gray-900">save and resume</span> at any
              time — close the tab or sign out, and you'll pick up right where you left off.
            </p>
          </li>
          <li className="flex items-start gap-3">
            <Clock size={16} className="text-brand-steel shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-gray-700 leading-relaxed">
              Plan for roughly{" "}
              <span className="font-medium text-gray-900">20–30 minutes</span> of focused time.
              There's no clock — take it at your own pace.
            </p>
          </li>
        </ul>
      </section>

      {/* Comparison disclosure — only for Sc.3 */}
      {hasComparison && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            Your results will include an anonymized comparison with other participants. All
            participant data is anonymized to protect individual identities.
          </p>
        </div>
      )}
    </div>
  );
}

export default AssessmentInstructionsContent;
